import React from 'react';
import { FiCalendar, FiUser, FiClock, FiAlertCircle } from 'react-icons/fi';
import { getTaskStatusColor, getTaskPriorityColor, isTaskOverdue, getDaysUntilDue } from '../../api/tasks';

const TaskCard = ({ task, onClick }) => {
  const statusColor = getTaskStatusColor(task.status);
  const priorityColor = getTaskPriorityColor(task.priority);
  const isOverdue = isTaskOverdue(task.due_date, task.due_time, task.status);
  const daysUntilDue = getDaysUntilDue(task.due_date);

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pending',
      in_progress: 'In Progress',
      on_hold: 'On Hold',
      completed: 'Completed',
      cancelled: 'Cancelled',
      closed: 'Closed'
    };
    return labels[status] || status;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      low: 'Low',
      medium: 'Medium',
      high: 'High',
      urgent: 'Urgent'
    };
    return labels[priority] || priority;
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div
      onClick={onClick}
      className={`relative bg-white rounded-lg shadow-sm border-2 hover:shadow-md transition-all cursor-pointer ${
        task.priority === 'urgent' || task.priority === 'high'
          ? 'border-2 border-accent-gold'
          : 'border-gray-200 hover:border-gray-300'
      } ${isOverdue ? 'ring-2 ring-red-500 ring-opacity-50' : ''}`}
    >
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-mono text-accent-gold font-semibold">
                {task.task_number}
              </span>
              {isOverdue && (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                  <FiAlertCircle className="mr-1 h-3 w-3" />
                  Overdue
                </span>
              )}
            </div>
            <h3 className="text-base font-semibold text-primary-black truncate">
              {task.title}
            </h3>
          </div>
          <span
            className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize"
            style={{
              backgroundColor: statusColor + '20',
              color: statusColor
            }}
          >
            {getStatusLabel(task.status)}
          </span>
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <p className="text-sm text-gray-600 line-clamp-2 mb-4">
          {task.description}
        </p>

        <div className="space-y-2">
          {/* Assigned To */}
          <div className="flex items-center text-sm text-gray-600">
            <FiUser className="mr-2 h-4 w-4 text-gray-400" />
            <span className="truncate">{task.assigned_to_name || 'Unassigned'}</span>
          </div>

          {/* Due Date */}
          <div className="flex items-center text-sm">
            <FiCalendar className="mr-2 h-4 w-4 text-gray-400" />
            <span className={isOverdue ? 'text-red-600 font-medium' : 'text-gray-600'}>
              {formatDate(task.due_date)}
              {task.due_time && ` at ${task.due_time.slice(0, 5)}`}
            </span>
            {daysUntilDue !== null && !isOverdue && daysUntilDue <= 3 && (
              <span className="ml-2 text-xs text-accent-gold">
                ({daysUntilDue === 0 ? 'Today' : `${daysUntilDue} day${daysUntilDue > 1 ? 's' : ''}`})
              </span>
            )}
          </div>

          {/* Category */}
          {task.category && (
            <div className="flex items-center text-sm text-gray-600">
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                {task.category}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span
              className="inline-flex items-center px-2 py-1 rounded text-xs font-medium capitalize"
              style={{
                backgroundColor: priorityColor + '15',
                color: priorityColor
              }}
            >
              {getPriorityLabel(task.priority)}
            </span>
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <FiClock className="mr-1 h-3 w-3" />
            {formatDate(task.created_at)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskCard;
